import React, { useState, useEffect } from 'react';
import { Search, Calendar, CheckSquare, Square, RefreshCw, X, AlertCircle, AlertTriangle, Database } from 'lucide-react';

const BatchUpdate = () => {
  // State for lists
  const [poList, setPoList] = useState([]);
  const [waveList, setWaveList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  
  // Selection state - now supports multiple selections
  const [selectedPOs, setSelectedPOs] = useState(new Set());
  const [selectedWaves, setSelectedWaves] = useState(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [newDueDate, setNewDueDate] = useState('');
  
  // State for accuracy modal
  const [isAccuracyModalOpen, setIsAccuracyModalOpen] = useState(false);
  
  // State for search criteria
  const [searchCriteria, setSearchCriteria] = useState({
    po: '',
    wave: ''
  });
  
  // State for wildcard search options
  const [findAllLikePO, setFindAllLikePO] = useState(false);
  const [findAllLikeWave, setFindAllLikeWave] = useState(false);
  
  // Get API URL from environment or default
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Function to execute search for POs with exact matching for better index usage
  const searchPOs = async () => {
    if (!searchCriteria.po.trim()) {
      setWarning("Please enter a PO number to search");
      return;
    }
    
    try {
      setSearchInProgress(true);
      setError(null);
      setWarning(null);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      // Add wildcard if "Find ALL like PO's" is checked
      const poValue = findAllLikePO && searchCriteria.po ? `${searchCriteria.po}%` : searchCriteria.po;
      queryParams.append('po', poValue);

      // Ask the backend to aggregate distinct POs so we get every match,
      // not just those appearing in the first page of order rows.
      const response = await fetch(`${API_URL}/orders/distinct-pos?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();

      const uniquePOs = data.records.map(({ value, count }) => ({
        value,
        count,
        totalCount: count
      }));

      setPoList(uniquePOs);
      
      // Clear wave selections when searching POs
      setSelectedWaves(new Set());
      
      // Set warning if provided by the API
      if (data.warning) {
        setWarning(data.warning);
      }
      
      setSearchInProgress(false);
    } catch (err) {
      setError(err.message || 'Failed to search for POs');
      setSearchInProgress(false);
    }
  };
  
  // Function to execute search for Waves with exact matching for better index usage
  const searchWaves = async () => {
    if (!searchCriteria.wave.trim()) {
      setWarning("Please enter a Wave to search");
      return;
    }
    
    try {
      setSearchInProgress(true);
      setError(null);
      setWarning(null);
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Build query parameters
      const queryParams = new URLSearchParams();
      // Add wildcard if "Find ALL like Waves" is checked
      const waveValue = findAllLikeWave && searchCriteria.wave ? `${searchCriteria.wave}%` : searchCriteria.wave;
      queryParams.append('wave', waveValue);

      const response = await fetch(`${API_URL}/orders/distinct-waves?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();

      const uniqueWaves = data.records.map(({ value, count }) => ({
        value,
        count,
        totalCount: count
      }));

      setWaveList(uniqueWaves);
      
      // Clear PO selections when searching waves
      setSelectedPOs(new Set());
      
      // Set warning if provided by the API
      if (data.warning) {
        setWarning(data.warning);
      }
      
      setSearchInProgress(false);
    } catch (err) {
      setError(err.message || 'Failed to search for Waves');
      setSearchInProgress(false);
    }
  };
  
  // Handle search criteria change
  const handleSearchCriteriaChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any warnings when criteria change
    setWarning(null);
  };
  
  // Clear all search criteria
  const clearSearchCriteria = () => {
    setSearchCriteria({
      po: '',
      wave: ''
    });
    setPoList([]);
    setWaveList([]);
    setSelectedPOs(new Set());
    setSelectedWaves(new Set());
    setWarning(null);
    setFindAllLikePO(false);
    setFindAllLikeWave(false);
  };
  
  // Toggle PO selection
  const togglePOSelection = (po) => {
    const newSelected = new Set(selectedPOs);
    if (newSelected.has(po)) {
      newSelected.delete(po);
    } else {
      newSelected.add(po);
    }
    setSelectedPOs(newSelected);
    // Clear wave selections when selecting POs
    setSelectedWaves(new Set());
  };
  
  // Toggle Wave selection
  const toggleWaveSelection = (wave) => {
    const newSelected = new Set(selectedWaves);
    if (newSelected.has(wave)) {
      newSelected.delete(wave);
    } else {
      newSelected.add(wave);
    }
    setSelectedWaves(newSelected);
    // Clear PO selections when selecting waves
    setSelectedPOs(new Set());
  };
  
  // Select all POs
  const selectAllPOs = () => {
    const allPOs = new Set(poList.map(item => item.value));
    setSelectedPOs(allPOs);
    setSelectedWaves(new Set());
  };
  
  // Select all Waves
  const selectAllWaves = () => {
    const allWaves = new Set(waveList.map(item => item.value));
    setSelectedWaves(allWaves);
    setSelectedPOs(new Set());
  };
  
  // Open edit modal for selected items
  const openEditModal = () => {
    if (selectedPOs.size === 0 && selectedWaves.size === 0) {
      setWarning("Please select at least one PO or Wave to update");
      return;
    }
    
    setNewDueDate('');
    setIsEditModalOpen(true);
  };
  
  // Close edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setNewDueDate('');
  };
  
  // Open accuracy modal
  const openAccuracyModal = () => {
    if (selectedPOs.size === 0 && selectedWaves.size === 0) {
      setWarning("Please select at least one PO or Wave to update accuracy");
      return;
    }
    setIsAccuracyModalOpen(true);
  };
  
  // Close accuracy modal
  const closeAccuracyModal = () => {
    setIsAccuracyModalOpen(false);
  };
  
  // Update accuracy for selected POs or Waves
  const updateAccuracy = async (isAccurate) => {
    if (selectedPOs.size === 0 && selectedWaves.size === 0) return;
    
    try {
      setUpdateInProgress(true);
      setUpdateStatus("Processing accuracy update request...");
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Process each selected item
      const isUpdatingPOs = selectedPOs.size > 0;
      const selectedItems = isUpdatingPOs ? Array.from(selectedPOs) : Array.from(selectedWaves);
      const updateByField = isUpdatingPOs ? 'PO' : 'Wave';
      
      // Calculate total count for all selected items
      let totalCount = 0;
      selectedItems.forEach(item => {
        const itemData = isUpdatingPOs ? 
          poList.find(po => po.value === item) : 
          waveList.find(wave => wave.value === item);
        totalCount += itemData?.totalCount || itemData?.count || 0;
      });
      
      setUpdateStatus(`Updating accuracy for ${totalCount} orders across ${selectedItems.length} ${updateByField}(s)...`);
      
      // Process updates for each selected item
      let successCount = 0;
      const errors = [];
      
      for (const fieldValue of selectedItems) {
        try {
          const response = await fetch(`${API_URL}/orders/update-accuracy-by-filter`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              filters: {
                [updateByField === 'PO' ? 'po' : 'wave']: fieldValue
              },
              isAccurate: isAccurate
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            errors.push(`${fieldValue}: ${errorData.message || 'Failed'}`);
          } else {
            const data = await response.json();
            successCount += data.updatedCount || 0;
          }
        } catch (err) {
          errors.push(`${fieldValue}: ${err.message}`);
        }
      }
      
      // Show results
      if (errors.length > 0) {
        setError(`Some updates failed: ${errors.join(', ')}`);
      } else {
        setWarning(`Successfully marked ${successCount} orders as ${isAccurate ? 'accurate' : 'inaccurate'}`);
      }
      
      // Clear selections
      setSelectedPOs(new Set());
      setSelectedWaves(new Set());
      closeAccuracyModal();
      
      // Reset search lists
      setPoList([]);
      setWaveList([]);
      
    } catch (err) {
      setError(err.message || 'Failed to update accuracy');
    } finally {
      setUpdateInProgress(false);
      setUpdateStatus(null);
    }
  };
  
  // Update due date for selected POs or Waves
  const updateDueDate = async () => {
    if ((selectedPOs.size === 0 && selectedWaves.size === 0) || !newDueDate) return;
    
    try {
      setUpdateInProgress(true);
      setUpdateStatus("Processing update request...");
      
      // Get token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Process each selected item
      const isUpdatingPOs = selectedPOs.size > 0;
      const selectedItems = isUpdatingPOs ? Array.from(selectedPOs) : Array.from(selectedWaves);
      const updateByField = isUpdatingPOs ? 'PO' : 'Wave';
      
      // Calculate total count for all selected items
      let totalCount = 0;
      selectedItems.forEach(item => {
        const itemData = isUpdatingPOs ? 
          poList.find(po => po.value === item) : 
          waveList.find(wave => wave.value === item);
        totalCount += itemData?.totalCount || itemData?.count || 0;
      });
      
      setUpdateStatus(`Updating due dates for ${totalCount} orders across ${selectedItems.length} ${updateByField}(s)...`);
      
      // Process updates for each selected item
      let successCount = 0;
      const errors = [];
      
      for (const fieldValue of selectedItems) {
        try {
          const response = await fetch(`${API_URL}/orders/update-due-dates-by-filter`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              filters: {
                [updateByField === 'PO' ? 'po' : 'wave']: fieldValue
              },
              newDueDate: newDueDate
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            errors.push(`${fieldValue}: ${errorData.message || 'Failed'}`);
          } else {
            const data = await response.json();
            successCount += data.updatedCount || 0;
          }
        } catch (err) {
          errors.push(`${fieldValue}: ${err.message}`);
        }
      }
      
      // Show results
      if (errors.length > 0) {
        setError(`Some updates failed: ${errors.join(', ')}`);
      } else {
        setWarning(`Successfully updated ${successCount} orders with the new due date`);
      }
      
      // Clear selections
      setSelectedPOs(new Set());
      setSelectedWaves(new Set());
      closeEditModal();
      
      // Reset search lists
      setPoList([]);
      setWaveList([]);
      
    } catch (err) {
      setError(err.message || 'Failed to update due dates');
    } finally {
      setUpdateInProgress(false);
      setUpdateStatus(null);
    }
  };
  
  // Calculate if any operation is in progress for the overlay
  const isOperationInProgress = loading || searchInProgress || updateInProgress;
  
  return (
    <div className="w-full h-full flex flex-col relative">
      {/* Loading Overlay - displayed during any operation */}
      {isOperationInProgress && (
        <div className="absolute inset-0 bg-slate-900/80 z-50 flex flex-col items-center justify-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          
          {loading && <p className="text-blue-200 text-lg font-medium">Loading data...</p>}
          {searchInProgress && <p className="text-blue-200 text-lg font-medium">Searching...</p>}
          
          {updateInProgress && (
            <>
              <p className="text-blue-200 text-lg font-medium mb-2">{updateStatus || "Updating due dates..."}</p>
            </>
          )}
        </div>
      )}
      
      {/* Header with title and actions */}
      <div className="p-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-200">Batch Update Order Due Date & Accuracy</h2>
          
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={clearSearchCriteria}
              className="text-xs px-2 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-slate-300 rounded"
              disabled={isOperationInProgress}
            >
              <X className="w-3 h-3 inline-block mr-1" />
              Clear
            </button>
            
            <button 
              onClick={openAccuracyModal}
              className="flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 
                      text-white rounded-md transition-colors text-sm"
              disabled={(selectedPOs.size === 0 && selectedWaves.size === 0) || isOperationInProgress}
            >
              <CheckSquare className="w-4 h-4 mr-1" />
              Update Accuracy {selectedPOs.size > 0 ? `(${selectedPOs.size} PO${selectedPOs.size > 1 ? 's' : ''})` : selectedWaves.size > 0 ? `(${selectedWaves.size} Wave${selectedWaves.size > 1 ? 's' : ''})` : ''}
            </button>
            
            <button 
              onClick={openEditModal}
              className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                      text-white rounded-md transition-colors text-sm"
              disabled={(selectedPOs.size === 0 && selectedWaves.size === 0) || isOperationInProgress}
            >
              <Calendar className="w-4 h-4 mr-1" />
              Update Due Date {selectedPOs.size > 0 ? `(${selectedPOs.size} PO${selectedPOs.size > 1 ? 's' : ''})` : selectedWaves.size > 0 ? `(${selectedWaves.size} Wave${selectedWaves.size > 1 ? 's' : ''})` : ''}
            </button>
            
            <button 
              onClick={clearSearchCriteria}
              className="flex items-center px-3 py-1.5 bg-slate-700 hover:bg-slate-600 
                      text-slate-300 rounded-md transition-colors text-sm"
              disabled={isOperationInProgress}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset
            </button>
          </div>
        </div>
      </div>
      
      {/* Warning banner for messages */}
      {warning && (
        <div className="mx-4 mt-2 p-2 bg-yellow-700/40 border border-yellow-600 rounded flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-yellow-300 text-sm">{warning}</p>
        </div>
      )}
      
      {/* Error display */}
      {error && (
        <div className="mx-4 mt-2 p-2 bg-red-700/40 border border-red-600 rounded flex items-start">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}
      
      {/* Search section */}
      <div className="p-4 border-b border-slate-700 bg-slate-700/20 flex-shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {/* PO Search */}
          <div className="bg-slate-800/30 rounded-md p-3">
            <h3 className="text-xs uppercase text-slate-500 mb-2">Search by Purchase Order</h3>
            <div className="grid grid-cols-1 gap-3">
              <div className="relative">
                <label htmlFor="po" className="block text-xs text-slate-400 mb-1">PO</label>
                <div className="flex">
                  <input
                    id="po"
                    type="text"
                    name="po"
                    value={searchCriteria.po}
                    onChange={handleSearchCriteriaChange}
                    placeholder="Enter PO number"
                    className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-l-md text-sm
                            text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                            focus:ring-blue-500"
                    disabled={isOperationInProgress}
                  />
                  <button
                    onClick={searchPOs}
                    className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                            text-xs text-white rounded-r-md transition-colors"
                    disabled={isOperationInProgress || !searchCriteria.po.trim()}
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Search
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-slate-400 flex items-center">
                    <Database className="w-3 h-3 inline mr-1" />
                    {findAllLikePO ? 'Using LIKE search (slower)' : 'Using PO index for fast retrieval'}
                  </div>
                  <label className="flex items-center text-xs text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={findAllLikePO}
                      onChange={(e) => setFindAllLikePO(e.target.checked)}
                      className="mr-1.5 h-3 w-3 rounded border-slate-600 bg-slate-700 text-blue-600 
                               focus:ring-1 focus:ring-blue-500 focus:ring-offset-0"
                      disabled={isOperationInProgress}
                    />
                    Find ALL like PO's
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          {/* Wave Search */}
          <div className="bg-slate-800/30 rounded-md p-3">
            <h3 className="text-xs uppercase text-slate-500 mb-2">Search by Wave</h3>
            <div className="grid grid-cols-1 gap-3">
              <div className="relative">
                <label htmlFor="wave" className="block text-xs text-slate-400 mb-1">Wave</label>
                <div className="flex">
                  <input
                    id="wave"
                    type="text"
                    name="wave"
                    value={searchCriteria.wave}
                    onChange={handleSearchCriteriaChange}
                    placeholder="Enter Wave number"
                    className="w-full pl-2 pr-6 py-1.5 bg-slate-700 border border-slate-600 rounded-l-md text-sm
                            text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 
                            focus:ring-blue-500"
                    disabled={isOperationInProgress}
                  />
                  <button
                    onClick={searchWaves}
                    className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 
                            text-xs text-white rounded-r-md transition-colors"
                    disabled={isOperationInProgress || !searchCriteria.wave.trim()}
                  >
                    <Search className="w-3 h-3 mr-1" />
                    Search
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-slate-400 flex items-center">
                    <Database className="w-3 h-3 inline mr-1" />
                    {findAllLikeWave ? 'Using LIKE search (slower)' : 'Using Wave index for fast retrieval'}
                  </div>
                  <label className="flex items-center text-xs text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={findAllLikeWave}
                      onChange={(e) => setFindAllLikeWave(e.target.checked)}
                      className="mr-1.5 h-3 w-3 rounded border-slate-600 bg-slate-700 text-blue-600 
                               focus:ring-1 focus:ring-blue-500 focus:ring-offset-0"
                      disabled={isOperationInProgress}
                    />
                    Find ALL like Waves
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Results section with two tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 overflow-auto" style={{ height: 'calc(100% - 240px)' }}>
        {/* PO Results Table */}
        <div className="bg-slate-800/30 rounded-md p-3 flex flex-col h-full">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Purchase Orders {poList.length > 0 ? `(${poList.length})` : ''}</h3>
            {findAllLikePO && poList.length > 0 && (
              <button
                onClick={selectAllPOs}
                className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                disabled={isOperationInProgress}
              >
                Select All
              </button>
            )}
          </div>
          
          <div className="overflow-auto flex-grow">
            {poList.length > 0 ? (
              <table className="w-full text-left">
                <thead className="bg-slate-700/50 text-slate-300 text-xs uppercase sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 whitespace-nowrap">Select</th>
                    <th className="px-2 py-2 whitespace-nowrap">PO</th>
                    <th className="px-2 py-2 whitespace-nowrap">Order Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {poList.map((item) => (
                    <tr 
                      key={item.value} 
                      className={`text-sm ${selectedPOs.has(item.value) ? 'bg-blue-700/30' : ''} hover:bg-slate-700/30 cursor-pointer`}
                      onClick={() => togglePOSelection(item.value)}
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">
                        <div className="flex justify-center">
                          {selectedPOs.has(item.value) ? (
                            <CheckSquare className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">{item.value}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">{item.totalCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                {searchCriteria.po ? 'No matching Purchase Orders found' : 'Search for a Purchase Order to see results'}
              </div>
            )}
          </div>
        </div>
        
        {/* Wave Results Table */}
        <div className="bg-slate-800/30 rounded-md p-3 flex flex-col h-full">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-slate-300">Waves {waveList.length > 0 ? `(${waveList.length})` : ''}</h3>
            {findAllLikeWave && waveList.length > 0 && (
              <button
                onClick={selectAllWaves}
                className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                disabled={isOperationInProgress}
              >
                Select All
              </button>
            )}
          </div>
          
          <div className="overflow-auto flex-grow">
            {waveList.length > 0 ? (
              <table className="w-full text-left">
                <thead className="bg-slate-700/50 text-slate-300 text-xs uppercase sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 whitespace-nowrap">Select</th>
                    <th className="px-2 py-2 whitespace-nowrap">Wave</th>
                    <th className="px-2 py-2 whitespace-nowrap">Order Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {waveList.map((item) => (
                    <tr 
                      key={item.value} 
                      className={`text-sm ${selectedWaves.has(item.value) ? 'bg-blue-700/30' : ''} hover:bg-slate-700/30 cursor-pointer`}
                      onClick={() => toggleWaveSelection(item.value)}
                    >
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">
                        <div className="flex justify-center">
                          {selectedWaves.has(item.value) ? (
                            <CheckSquare className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">{item.value}</td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-slate-300">{item.totalCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                {searchCriteria.wave ? 'No matching Waves found' : 'Search for a Wave to see results'}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Edit Due Date Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-lg max-w-md w-full border border-slate-700">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">Update Due Date</h3>
              <button 
                onClick={closeEditModal}
                className="text-slate-400 hover:text-slate-300"
                disabled={updateInProgress}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-slate-400 mb-2">Selected Items:</p>
                <p className="text-slate-300 mb-3">
                  {selectedPOs.size > 0 && (
                    <>
                      Updating due date for {selectedPOs.size} Purchase Order{selectedPOs.size > 1 ? 's' : ''}
                      <br/>
                      <span className="text-blue-300">
                        {Array.from(selectedPOs).reduce((total, po) => {
                          const item = poList.find(p => p.value === po);
                          return total + (item?.totalCount || item?.count || 0);
                        }, 0)} total orders will be updated
                      </span>
                    </>
                  )}
                  {selectedWaves.size > 0 && (
                    <>
                      Updating due date for {selectedWaves.size} Wave{selectedWaves.size > 1 ? 's' : ''}
                      <br/>
                      <span className="text-blue-300">
                        {Array.from(selectedWaves).reduce((total, wave) => {
                          const item = waveList.find(w => w.value === wave);
                          return total + (item?.totalCount || item?.count || 0);
                        }, 0)} total orders will be updated
                      </span>
                    </>
                  )}
                </p>
                
                <div className="mb-3 p-2 bg-blue-900/30 border border-blue-800 rounded-md text-blue-300 text-xs">
                  <Database className="inline-block w-3 h-3 mb-0.5 mr-1" />
                  Using filter-based update for improved performance with large data sets
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="newDueDate" className="block text-sm font-medium text-slate-300 mb-1">
                  New Due Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="newDueDate"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    className="w-full pl-3 pr-8 py-1.5 bg-slate-700 border border-slate-600 rounded-md
                             text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    disabled={updateInProgress}
                  />
                  <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                </div>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeEditModal}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={updateDueDate}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
                  disabled={!newDueDate || updateInProgress}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Accuracy Update Modal */}
      {isAccuracyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg shadow-lg max-w-md w-full border border-slate-700">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-slate-200">Update Accuracy</h3>
              <button 
                onClick={closeAccuracyModal}
                className="text-slate-400 hover:text-slate-300"
                disabled={updateInProgress}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-slate-400 mb-2">Selected Items:</p>
                <p className="text-slate-300 mb-3">
                  {selectedPOs.size > 0 && (
                    <>
                      Updating accuracy for {selectedPOs.size} Purchase Order{selectedPOs.size > 1 ? 's' : ''}
                      <br/>
                      <span className="text-blue-300">
                        {Array.from(selectedPOs).reduce((total, po) => {
                          const item = poList.find(p => p.value === po);
                          return total + (item?.totalCount || item?.count || 0);
                        }, 0)} total orders will be updated
                      </span>
                    </>
                  )}
                  {selectedWaves.size > 0 && (
                    <>
                      Updating accuracy for {selectedWaves.size} Wave{selectedWaves.size > 1 ? 's' : ''}
                      <br/>
                      <span className="text-blue-300">
                        {Array.from(selectedWaves).reduce((total, wave) => {
                          const item = waveList.find(w => w.value === wave);
                          return total + (item?.totalCount || item?.count || 0);
                        }, 0)} total orders will be updated
                      </span>
                    </>
                  )}
                </p>
                
                <p className="text-slate-300 mb-4">
                  Would you like to mark these orders as accurate or inaccurate?
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeAccuracyModal}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={() => updateAccuracy(false)}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Mark Inaccurate
                </button>
                <button
                  onClick={() => updateAccuracy(true)}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm"
                  disabled={updateInProgress}
                >
                  Mark Accurate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchUpdate;
